/**
 * Bước xác thực trước khi vào thi có giám sát (TT 83 C1-d; TT 56 Đ5.4).
 *
 * Render khi `POST /assessment/attempts/start/` trả 403 với `code` thuộc
 * `IDENTITY_GATE_CODES`. Chụp selfie → đối chiếu với ảnh eKYC → đạt thì gọi `onPassed`
 * (QuizConsent thử bắt đầu lại); chờ duyệt thì GV coi thi duyệt tay ở /admin/identity.
 */
import { useState } from "react";
import { Alert, Button, Card, Result, Space, Typography, message } from "antd";
import { SafetyCertificateOutlined } from "@ant-design/icons";
import { useMutation } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import {
  type IdentityGateCode,
  errorDetail,
  submitExamFaceMatch,
} from "@/features/identity/api";
import { ImageCapture } from "@/features/identity/ImageCapture";

const { Title, Paragraph } = Typography;

export function ExamIdentityCheck({
  quizId,
  code,
  onPassed,
}: {
  quizId: number;
  code: IdentityGateCode;
  onPassed: () => void;
}) {
  const [selfie, setSelfie] = useState<File | null>(null);
  const [pending, setPending] = useState(code === "face_match_pending");

  const mutation = useMutation({
    mutationFn: () => submitExamFaceMatch(quizId, selfie!),
    onSuccess: (res) => {
      setSelfie(null);
      if (res.status === "passed") {
        message.success("Đối chiếu khuôn mặt đạt");
        onPassed();
      } else if (res.status === "needs_review") {
        setPending(true);
      } else {
        message.warning(res.reason || "Không khớp — vui lòng chụp lại rõ mặt, đủ sáng.");
      }
    },
    onError: (err) => message.error(errorDetail(err, "Không gửi được ảnh đối chiếu")),
  });

  if (code === "ekyc_required") {
    return (
      <Result
        status="warning"
        title="Cần xác thực danh tính trước khi thi"
        subTitle="Bài thi này yêu cầu bạn đã hoàn tất xác thực danh tính (eKYC) với trường."
        extra={
          <Link to="/identity-verify">
            <Button type="primary">Đi tới xác thực danh tính</Button>
          </Link>
        }
      />
    );
  }

  return (
    <Card style={{ maxWidth: 640, margin: "0 auto" }} data-testid="exam-identity-check">
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <Title level={3} style={{ margin: 0 }}>
          <SafetyCertificateOutlined /> Đối chiếu khuôn mặt trước khi thi
        </Title>
        {pending ? (
          <>
            <Alert
              type="info"
              showIcon
              message="Ảnh đối chiếu đang chờ giảng viên coi thi duyệt."
              description="Giữ trang này mở. Khi được duyệt, bấm Kiểm tra lại để vào thi."
            />
            <Button type="primary" onClick={onPassed}>
              Kiểm tra lại
            </Button>
          </>
        ) : (
          <>
            {code === "face_match_failed" && (
              <Alert type="warning" showIcon message="Lần đối chiếu trước không đạt — chụp lại." />
            )}
            <Paragraph style={{ margin: 0 }}>
              Ảnh được so với ảnh khuôn mặt bạn đã đăng ký khi xác thực danh tính. Nhìn thẳng
              camera, đủ sáng, không đeo khẩu trang/kính râm.
            </Paragraph>
            <ImageCapture
              label="Ảnh khuôn mặt"
              facing="user"
              value={selfie}
              onChange={setSelfie}
              testId="input-exam-selfie"
            />
            <Button
              type="primary"
              disabled={!selfie}
              loading={mutation.isPending}
              onClick={() => mutation.mutate()}
            >
              Gửi ảnh đối chiếu
            </Button>
          </>
        )}
      </Space>
    </Card>
  );
}
